import {Component, inject, OnInit} from "@angular/core";
import {map} from "rxjs";
import {noWhitespaceValidator, numberNotBiggerThan190000m, percentRangeValidator, percentWarningMessage} from "../../../shared/utils/form-validators";
import {Store} from "@ngrx/store";
import {FormBuilder, Validators} from "@angular/forms";
import {DecimalFormatPipe} from "../../../shared/pipes/decimal-format/decimal-format.pipe";
import {selectDealDetails, selectIsAnyDealFormInEditMode} from "../../store/deal/deal.selectors";
import {DealDetailsAction} from "../../store/deal.actions";
import {Deal} from "../../models/deal";
import {CodeTableEnum} from "../../../shared/model/code";
import {selectSelectedSeller} from "../../store/seller/seller.selectors";

@Component({
    selector: "valumize-deal-details",
    templateUrl: "./deal-details.component.html",
    standalone: false
})
export class DealDetailsComponent implements OnInit {
    private readonly store = inject(Store);
    private readonly formBuilder = inject(FormBuilder);

    codeTableCurrency = CodeTableEnum.SHARED_CURRENCY;
    codeTableProcessType = CodeTableEnum.PROCESS_TYPE;

    dealDetails$ = this.store.select(selectDealDetails);
    seller$ = this.store.select(selectSelectedSeller);
    isEditDisabled$ = this.store.select(selectIsAnyDealFormInEditMode);

    dealDetailsForm = this.formBuilder.group({
        secondaryCarry: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m(), percentRangeValidator("percent")]}),
        secondaryHurdle: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m(), percentRangeValidator("percent")]}),
        secondaryManagementFee: this.formBuilder.control<number | null>(null, {validators: [numberNotBiggerThan190000m(), percentRangeValidator("percent")]}),
        currencyIso: this.formBuilder.control<string>("", {nonNullable: true, validators: [Validators.required, noWhitespaceValidator()]}),
        source: this.formBuilder.control<string>("", {validators: Validators.maxLength(100)}),
        processType: this.formBuilder.control<string>("", {nonNullable: true}),
        dealCaptain: this.formBuilder.control<string>("", {validators: Validators.maxLength(100)})
    });

    constructor( ) {
    }

    ngOnInit(): void {
        this.store.select(selectDealDetails).pipe(
            map(dealDetails => {
                const deal = dealDetails.deal;
                this.dealDetailsForm.patchValue({
                    secondaryCarry: DecimalFormatPipe.transformFractionToPercent(deal.secondaryCarry.fraction ?? undefined),
                    secondaryHurdle: DecimalFormatPipe.transformFractionToPercent(deal.secondaryHurdle.fraction ?? undefined),
                    secondaryManagementFee: DecimalFormatPipe.transformFractionToPercent(deal.secondaryManagementFee.fraction ?? undefined),
                    currencyIso: deal.currencyIso.code,
                    source: deal.source.text,
                    processType: deal.processType.code,
                    dealCaptain: deal.dealCaptain.text
                });
            })
        ).subscribe();
    }

    /** Non-blocking "unusually high" caution for a fee/carry/hurdle control (values are percentages). */
    feeWarning(controlName: "secondaryManagementFee" | "secondaryCarry" | "secondaryHurdle", thresholdPercent: number): string | null {
        return percentWarningMessage(this.dealDetailsForm.controls[controlName].value, thresholdPercent, "percent");
    }

    edit = () => this.store.dispatch(DealDetailsAction.edit());

    save(deal: Deal): void {
        if (!deal) {
            return;
        }
        const formValuesToSave = Object.assign({}, this.dealDetailsForm.getRawValue());
        this.store.dispatch(DealDetailsAction.save({deal, ...formValuesToSave}));
    }

    cancel = () => this.store.dispatch(DealDetailsAction.cancel());
}
